/**
 * The editor host hooks the console installs while Pages is open: who a new
 * decision or open-question mark is by (the signed-in member's roster name and
 * email), and what a citation chip can open here. A cited session lives on its
 * author's desktop, so the chip shows its snapshot quote and offers no jump; a
 * cited source opens in this tab when it is a console link, in a new tab when
 * it is anywhere else. A chip for someone's Personal page opens its console
 * link here too, which lands on the open-in-app page. An `@` reference to a
 * team page opens that page here, or in a new tab on Cmd/Ctrl or middle click.
 */
import { setCitationHost, setConsoleLinkOpener, setPageMarkAuthorProvider, setPageReferenceOpener } from '@nimbalyst/collab-bundle/editor';
import { consolePathForHref } from './pagesLinks';

export interface WebEditorHostHooksOptions {
  /** Read when a mark is made, so a roster that answers late still counts. */
  getAuthor: () => { name: string | null; email: string | null } | null;
  navigate: (path: string) => void;
  /** The route of a team page in this project. */
  pagePath: (documentId: string) => string;
  origin?: string;
  openExternal?: (url: string) => void;
}

export function installWebEditorHostHooks(options: WebEditorHostHooksOptions): () => void {
  const origin = options.origin ?? window.location.origin;
  const openExternal = options.openExternal ?? ((url: string) => { window.open(url, '_blank', 'noopener,noreferrer'); });
  setPageMarkAuthorProvider(() => options.getAuthor());
  setCitationHost({
    canOpenHumanCitation: () => false,
    openSource: (target) => {
      const path = consolePathForHref(target, origin);
      if (path) options.navigate(path);
      else if (/^https?:/i.test(target)) openExternal(target);
    },
  });
  const removeOpener = setConsoleLinkOpener((href) => {
    const path = consolePathForHref(href, origin);
    if (path) options.navigate(path);
    return path !== null;
  });
  const removePageReferenceOpener = setPageReferenceOpener((documentId, { newTab }) => {
    const path = options.pagePath(documentId);
    if (newTab) openExternal(new URL(path, origin).href);
    else options.navigate(path);
  });
  return () => {
    setPageMarkAuthorProvider(undefined);
    setCitationHost(undefined);
    removeOpener();
    removePageReferenceOpener();
  };
}
